import React, { useState, useEffect, useCallback } from "react";
import {
  X, Loader2, MapPin, Store, Paintbrush, Sparkles, UserCheck, AlertTriangle,
  CheckCircle, History, Clock, Image as ImageIcon,
} from "lucide-react";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_STYLE = {
  fully_assigned: { label: "Fully Assigned", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  partially_assigned: { label: "Partially Assigned", cls: "bg-amber-50 text-amber-700 border-amber-200" },
  awaiting_ops_review: { label: "Awaiting OPS Review", cls: "bg-blue-50 text-blue-700 border-blue-200" },
  no_vendor_available: { label: "No Vendor Available", cls: "bg-red-50 text-red-700 border-red-200" },
  pending: { label: "Pending", cls: "bg-gray-100 text-gray-600 border-gray-200" },
  reassignment_required: { label: "Reassignment Required", cls: "bg-orange-50 text-orange-700 border-orange-200" },
};

const StatusPill = ({ status }) => {
  const s = STATUS_STYLE[status] || STATUS_STYLE.pending;
  return <span data-testid="vendor-status-pill" className={`px-2.5 py-1 rounded-full text-[11px] font-bold border ${s.cls}`}>{s.label}</span>;
};

const EvidenceThumbs = ({ authAxios, assignmentId, evidence }) => {
  const [urls, setUrls] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    const created = [];
    (async () => {
      setLoading(true);
      const out = [];
      for (const e of evidence) {
        try {
          const r = await authAxios.get(
            `${API_URL}/api/scheduler/vendors/assignment/${assignmentId}/evidence/${e.evidence_id}`,
            { responseType: "blob" }
          );
          const url = URL.createObjectURL(r.data);
          created.push(url);
          out.push({ id: e.evidence_id, url, uploaded_at: e.uploaded_at });
        } catch (err) { /* skip unreadable evidence */ }
      }
      if (active) { setUrls(out); setLoading(false); }
    })();
    return () => { active = false; created.forEach((u) => URL.revokeObjectURL(u)); };
  }, [authAxios, assignmentId, evidence]);

  return (
    <div className="mt-2" data-testid={`vendor-evidence-${assignmentId}`}>
      <p className="text-[10px] font-bold text-[#64748B] uppercase tracking-wide mb-1.5 flex items-center gap-1">
        <ImageIcon size={11} /> Completion Photos ({evidence.length})
      </p>
      {loading ? (
        <div className="flex items-center gap-1.5 text-[10px] text-[#94a3b8]"><Loader2 size={11} className="animate-spin" /> Loading photos…</div>
      ) : urls.length === 0 ? (
        <p className="text-[10px] text-[#94a3b8]">Could not load the uploaded photos.</p>
      ) : (
        <div className="flex flex-wrap gap-2">
          {urls.map((u) => (
            <button key={u.id} type="button" data-testid={`evidence-thumb-${u.id}`}
              onClick={() => window.open(u.url, "_blank", "noopener")}
              title="Open full size in a new tab"
              className="relative group rounded-lg overflow-hidden border border-gray-200 hover:ring-2 hover:ring-[#7C3AED]/40 focus:outline-none">
              <img src={u.url} alt="Completion evidence" className="h-16 w-16 object-cover" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const RoleSection = ({ section, mandatory, busy, onConfirm, onUnassign, onVerify, authAxios }) => {
  const [sel, setSel] = useState("");
  const current = section.assigned;
  const Icon = section.role === "mandatory_balloon_shop" ? Store : section.role === "mandatory_decorator" ? Paintbrush : Sparkles;
  return (
    <div className={`rounded-xl border p-3.5 ${section.blocked ? "border-red-200 bg-red-50/40" : "border-gray-100"}`} data-testid={`vendor-role-${section.role}`}>
      <div className="flex items-center justify-between mb-2">
        <p className="text-sm font-bold text-[#1E1B4B] flex items-center gap-2"><Icon size={15} className="text-[#7C3AED]" /> {section.label}
          {mandatory && <span className="text-[9px] uppercase tracking-wide text-red-500 font-extrabold">Mandatory</span>}</p>
        {current && <span className="flex items-center gap-1 text-[11px] font-bold text-emerald-700"><CheckCircle size={12} /> Assigned</span>}
      </div>

      {current ? (
        <div className="rounded-lg bg-white border border-gray-100 px-3 py-2">
          <div className="flex items-center justify-between gap-2">
            <div className="min-w-0">
              <p className="text-xs font-bold text-[#1E1B4B] truncate">{current.name}</p>
              <p className="text-[10px] text-[#94a3b8]">Pincode {current.pincode} · {current.phone || "—"}</p>
              {current.status && current.status !== "assigned" && (
                <span className="text-[9px] font-bold uppercase tracking-wide text-[#7C3AED]" data-testid={`assign-status-${section.role}`}>{String(current.status).replace(/_/g, " ")}</span>
              )}
            </div>
            <div className="flex items-center gap-1.5">
              {current.status === "submitted" && (
                <button data-testid={`vendor-verify-${section.role}`} disabled={busy}
                  onClick={() => onVerify(current.assignment_id)}
                  className="px-2.5 py-1 rounded-lg bg-[#10B981] text-white text-[10px] font-bold hover:bg-[#059669] disabled:opacity-40">Verify</button>
              )}
              <button data-testid={`vendor-unassign-${section.role}`} disabled={busy}
                onClick={() => onUnassign(section.role)}
                className="px-2.5 py-1 rounded-lg border border-gray-200 text-[10px] font-bold text-[#DC283C] hover:bg-red-50 disabled:opacity-40">Reassign</button>
            </div>
          </div>
          {(current.evidence || []).length > 0 && (
            <EvidenceThumbs authAxios={authAxios} assignmentId={current.assignment_id} evidence={current.evidence} />
          )}
        </div>
      ) : section.blocked ? (
        <div className="flex items-center gap-2 text-[11px] font-semibold text-red-600" data-testid={`vendor-blocked-${section.role}`}>
          <AlertTriangle size={13} /> No approved vendor serves this exact pincode. Flagged for OPS intervention.
        </div>
      ) : section.candidates.length === 0 ? (
        <p className="text-[11px] text-[#94a3b8]">No eligible vendors in this pincode yet.</p>
      ) : (
        <div className="flex flex-wrap items-center gap-2">
          <select value={sel} onChange={(e) => setSel(e.target.value)} data-testid={`vendor-select-${section.role}`}
            className="flex-1 min-w-[180px] px-2.5 py-1.5 rounded-lg border border-gray-200 text-[11px] font-medium text-[#1E1B4B] bg-white focus:outline-none focus:ring-2 focus:ring-[#7C3AED]/30">
            <option value="">Select a vendor to assign…</option>
            {section.candidates.map((c) => (
              <option key={c.vendor_id} value={c.vendor_id} disabled={!c.available}>
                {`${c.name} · ${c.rating || 0}★ · ${c.load}/${c.capacity}${c.available ? "" : " — unavailable"}`}
              </option>
            ))}
          </select>
          <button data-testid={`vendor-confirm-${section.role}`} disabled={busy || !sel}
            onClick={() => onConfirm(section.role, sel)}
            className="flex items-center gap-1 px-3 py-1.5 bg-[#10B981] text-white rounded-lg text-[10px] font-bold hover:bg-[#059669] disabled:opacity-40">
            {busy ? <Loader2 size={11} className="animate-spin" /> : <UserCheck size={11} />} Confirm
          </button>
        </div>
      )}

      {!current && section.candidates.length > 0 && (
        <p className="text-[10px] text-[#94a3b8] mt-1.5">{section.candidates.length} eligible vendor(s) · recommend-only, OPS confirms. Same-pincode is enforced.</p>
      )}
    </div>
  );
};

const VendorAssignmentModal = ({ bookingId, authAxios, onClose, onChanged }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await authAxios.get(`${API_URL}/api/scheduler/vendors/recommendations`, { params: { booking_id: bookingId } });
      setData(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load vendor recommendations");
    }
    setLoading(false);
  }, [authAxios, bookingId]);

  useEffect(() => { load(); }, [load]);

  const confirm = async (role, vendorId) => {
    setBusy(true);
    try {
      await authAxios.post(`${API_URL}/api/scheduler/vendors/confirm`, { booking_id: bookingId, vendor_id: vendorId, role });
      toast.success("Vendor confirmed");
      await load(); onChanged && onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not confirm vendor"); }
    setBusy(false);
  };

  const unassign = async (role) => {
    setBusy(true);
    try {
      await authAxios.post(`${API_URL}/api/scheduler/vendors/unassign`, { booking_id: bookingId, role });
      toast.success("Vendor removed — ready to reassign");
      await load(); onChanged && onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not remove vendor"); }
    setBusy(false);
  };

  const verify = async (assignmentId) => {
    setBusy(true);
    try {
      await authAxios.post(`${API_URL}/api/scheduler/vendors/verify-completion`, { assignment_id: assignmentId });
      toast.success("Completion verified");
      await load(); onChanged && onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not verify completion"); }
    setBusy(false);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-start md:items-center justify-center p-3 overflow-y-auto" data-testid="vendor-assignment-modal">
      <div className="bg-white rounded-2xl w-full max-w-2xl my-6 shadow-2xl">
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-gray-100 sticky top-0 bg-white rounded-t-2xl">
          <h3 className="text-base font-bold text-[#1E1B4B]">Vendor Assignment</h3>
          <button onClick={onClose} data-testid="vendor-modal-close" className="p-1.5 rounded-lg hover:bg-gray-100"><X size={18} /></button>
        </div>

        {loading ? (
          <div className="py-16 flex justify-center"><Loader2 className="animate-spin text-[#DC283C]" size={26} /></div>
        ) : !data ? (
          <p className="text-sm text-center text-[#94a3b8] py-16">Could not load booking.</p>
        ) : (
          <div className="p-5 space-y-4">
            {/* 1. Booking Details */}
            <div className="rounded-xl border border-gray-100 p-3.5" data-testid="section-booking">
              <div className="flex items-center justify-between">
                <p className="text-sm font-bold text-[#1E1B4B]">{data.booking.customer_name} — {data.booking.package || "—"}</p>
                <StatusPill status={data.status.vendor_assignment_status} />
              </div>
              <p className="text-[11px] text-[#94a3b8] mt-1 flex items-center gap-2">
                <Clock size={11} /> {String(data.booking.date).slice(0, 10)} {data.booking.setup_time}
                <MapPin size={11} /> Pincode {data.pincode || "—"} · {data.booking.occasion || ""}
              </p>
            </div>

            {/* 2. OPS Assignment */}
            <div className="rounded-xl border border-gray-100 p-3.5" data-testid="section-ops">
              <p className="text-[11px] font-bold text-[#64748B] uppercase tracking-wide mb-1">OPS Assignment</p>
              {data.ops_assignment.name ? (
                <span className="flex items-center gap-1.5 text-xs font-bold text-emerald-700"><UserCheck size={13} /> {data.ops_assignment.name} <span className="text-[#94a3b8] font-medium">{data.ops_assignment.phone}</span></span>
              ) : <p className="text-xs text-amber-600 font-semibold">No OPS member assigned yet (assign from the Scheduler).</p>}
            </div>

            {/* 3. Mandatory Vendor Assignment */}
            <div className="space-y-2.5">
              <p className="text-[11px] font-bold text-[#64748B] uppercase tracking-wide">Mandatory Vendors (exact pincode)</p>
              {data.mandatory.map((m) => (
                <RoleSection key={m.role} section={m} mandatory busy={busy} onConfirm={confirm} onUnassign={unassign} onVerify={verify} authAxios={authAxios} />
              ))}
            </div>

            {/* 4. Optional Vendor Assignment */}
            <div className="space-y-2.5">
              <p className="text-[11px] font-bold text-[#64748B] uppercase tracking-wide">Optional Vendors (requested services only)</p>
              {data.optional.length === 0 ? (
                <p className="text-[11px] text-[#94a3b8]" data-testid="no-optional-requested">No optional services requested for this booking.</p>
              ) : data.optional.map((o) => (
                <RoleSection key={o.role} section={o} mandatory={false} busy={busy} onConfirm={confirm} onUnassign={unassign} onVerify={verify} authAxios={authAxios} />
              ))}
            </div>

            {/* 5. Assignment History & Issues */}
            <div className="rounded-xl border border-gray-100 p-3.5" data-testid="section-history">
              <p className="text-[11px] font-bold text-[#64748B] uppercase tracking-wide mb-2 flex items-center gap-1.5"><History size={12} /> Assignment History</p>
              {(data.history || []).length === 0 ? (
                <p className="text-[11px] text-[#94a3b8]">No vendor assignment activity yet.</p>
              ) : (
                <ul className="space-y-1.5 max-h-40 overflow-y-auto">
                  {data.history.map((h, i) => (
                    <li key={h.id || i} className="text-[11px] text-[#475569] flex items-center justify-between gap-2">
                      <span><b className="capitalize">{h.action}</b> {h.name ? `· ${h.name}` : ""} {h.role ? `(${h.role.replace("mandatory_", "").replace("optional_", "")})` : ""}</span>
                      <span className="text-[#94a3b8]">{String(h.at).slice(0, 16).replace("T", " ")} · {h.by}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default VendorAssignmentModal;
